import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductPath } from '../utils/productUrl';
import { formatOfferPrice } from '../utils/productOffers';
import {
  getOfferStatusForListing,
  getOfferStatusLabel,
  isOfferExpired,
} from '../utils/offerStatus';
import {
  computeSavedDealsTotals,
  getSavedDealLinePrices,
} from '../utils/savedDealsTotals';
import { buildSavedDealsShareUrl } from '../utils/myList';
import './MyListModal.css';

const MyListModal = ({ isOpen, onClose, items = [], onRemoveItem, onCheckAvailability }) => {
  const navigate = useNavigate();
  const [shareLabel, setShareLabel] = useState('Share');

  const totals = useMemo(() => computeSavedDealsTotals(items), [items]);
  const shareUrl = useMemo(() => buildSavedDealsShareUrl(items), [items]);

  useEffect(() => {
    if (!isOpen) setShareLabel('Share');
  }, [isOpen]);

  useEffect(() => {
    if (shareLabel === 'Share') return undefined;
    const timer = setTimeout(() => setShareLabel('Share'), 2000);
    return () => clearTimeout(timer);
  }, [shareLabel]);

  if (!isOpen) return null;

  const handleViewProduct = (product) => {
    onClose();
    navigate(getProductPath(product));
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        return true;
      }
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      return true;
    } catch {
      window.prompt('Copy this saved deals link:', shareUrl);
      return false;
    }
  };

  const handleShare = async () => {
    if (!shareUrl) return;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: 'Saved deals on Raheasy',
          text: 'Deals I saved on Raheasy',
          url: shareUrl,
        });
        setShareLabel('Shared');
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }
    const copied = await copyShareUrl();
    if (copied) setShareLabel('Copied');
  };

  return (
    <div className="my-list-overlay" onClick={onClose}>
      <div className="my-list-modal" onClick={(e) => e.stopPropagation()}>
        <div className="my-list-header">
          <h2>Saved Deals</h2>
          <div className="my-list-header-tools">
            {shareUrl ? (
              <button
                type="button"
                className="my-list-share-quiet"
                onClick={handleShare}
              >
                {shareLabel}
              </button>
            ) : null}
            <button type="button" className="close-my-list" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>
        <div className="my-list-body">
          {items.length === 0 ? (
            <div className="my-list-empty">
              <p>No saved deals yet</p>
              <p className="my-list-empty-hint">
                Tap the heart on any product card to save a deal here.
              </p>
              <a href="/" className="shop-now-btn" onClick={onClose}>
                Browse products
              </a>
            </div>
          ) : (
            <ul className="my-list-items">
              {items.map((product) => {
                const productId = product.id || product._id;
                const offerStatus = getOfferStatusForListing(product);
                const statusLabel = getOfferStatusLabel(offerStatus);
                const expired = isOfferExpired(product);
                const { dealPrice, originalPrice } = getSavedDealLinePrices(product);
                const showOriginal = originalPrice > dealPrice;
                const imageSrc =
                  product.thumbnail ||
                  product.image ||
                  (Array.isArray(product.images) && product.images.length > 0
                    ? product.images[product.primaryImageIndex || 0]
                    : '');
                const imageFallback =
                  product.thumbnailFallback ||
                  (Array.isArray(product.imageFallbacks) && product.imageFallbacks.length
                    ? product.imageFallbacks[product.primaryImageIndex || 0] || product.imageFallbacks[0]
                    : '');
                const imageKit =
                  product.thumbnailImagekit ||
                  (Array.isArray(product.imagekitImages) && product.imagekitImages.length
                    ? product.imagekitImages[product.primaryImageIndex || 0] || product.imagekitImages[0]
                    : '');

                return (
                  <li
                    key={productId}
                    className={`my-list-item${expired ? ' my-list-item--expired' : ''}`}
                  >
                    <button
                      type="button"
                      className="my-list-item-image-btn"
                      onClick={() => handleViewProduct(product)}
                      aria-label={`View ${product.name}`}
                    >
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt=""
                          className="my-list-item-image"
                          onError={(e) => {
                            const src = e.currentTarget.src || '';
                            if (imageKit && !/ik\.imagekit\.io/i.test(src) && !/\.blob\.vercel-storage\.com/i.test(src)) {
                              e.currentTarget.src = imageKit;
                              return;
                            }
                            if (imageFallback && !/\.blob\.vercel-storage\.com/i.test(src)) {
                              e.currentTarget.src = imageFallback;
                              return;
                            }
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="my-list-item-image my-list-item-image--placeholder" aria-hidden>
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                          </svg>
                        </div>
                      )}
                    </button>
                    <div className="my-list-item-info">
                      <button
                        type="button"
                        className="my-list-item-name"
                        onClick={() => handleViewProduct(product)}
                      >
                        {product.name}
                      </button>
                      {product.storeName ? (
                        <span className="my-list-item-store">{product.storeName}</span>
                      ) : null}
                      <div className="my-list-item-pricing">
                        <span className="my-list-deal-price">{formatOfferPrice(dealPrice)}</span>
                        {showOriginal ? (
                          <span className="my-list-original-price">
                            {formatOfferPrice(originalPrice)}
                          </span>
                        ) : null}
                        {statusLabel ? (
                          <span className={`my-list-status-badge my-list-status-badge--${offerStatus}`}>
                            {statusLabel}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="my-list-item-actions">
                      <button
                        type="button"
                        className="my-list-check-btn"
                        disabled={expired}
                        onClick={() => onCheckAvailability?.(product)}
                      >
                        {expired ? 'Unavailable' : 'Check Availability'}
                      </button>
                      <button
                        type="button"
                        className="my-list-remove-btn"
                        onClick={() => onRemoveItem?.(productId)}
                        aria-label={`Remove ${product.name} from saved deals`}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {items.length > 0 && totals.activeCount > 0 ? (
          <div className="my-list-summary" aria-live="polite">
            <div className="my-list-summary-compact-top">
              <span className="my-list-summary-meta">
                {totals.activeCount} {totals.activeCount === 1 ? 'deal' : 'deals'} · if you bought all
              </span>
              <span className="my-list-summary-save">
                Save {formatOfferPrice(totals.savedTotal)}
              </span>
            </div>
            <div className="my-list-summary-compact-bottom">
              <span>Deal total {formatOfferPrice(totals.dealTotal)}</span>
              <span className="my-list-summary-original-strike">
                {formatOfferPrice(totals.originalTotal)}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MyListModal;
